import { Component, EventEmitter, Input, Output } from '@angular/core';

export type FormShellLayout = 'page' | 'drawer' | 'dialog';

/**
 * Standard form frame: header (back on pages, close in drawers/dialogs), scrolling body and a
 * sticky footer with the "Required: ..." hint plus Cancel / Save.
 * Slots: `[shellHeader]` (extra header controls), default (body), `[shellFooterStart]`, `[shellFooter]`.
 */
@Component({
  selector: 'app-form-shell',
  template: `
    <div class="jf" [class.jf--drawer]="layout === 'drawer'" [class.jf--dialog]="layout === 'dialog'">
      <header class="jf-head" *ngIf="showHeader">
        <button
          *ngIf="layout === 'page' && showBack"
          type="button"
          class="jf-icon-btn jf-head__back"
          (click)="cancel.emit()"
          aria-label="Back">
          <i class="pi pi-arrow-left"></i>
        </button>
        <h2 class="jf-head__title">
          {{ title }}
          <small class="jf-head__sub" *ngIf="subtitle">{{ subtitle }}</small>
        </h2>
        <ng-content select="[shellHeader]"></ng-content>
        <button
          *ngIf="layout !== 'page'"
          type="button"
          class="jf-icon-btn jf-head__close"
          (click)="cancel.emit()"
          aria-label="Close">
          <i class="pi pi-times"></i>
        </button>
      </header>

      <div class="jf-body" *ngIf="!loading; else loadingTpl">
        <ng-content></ng-content>
      </div>

      <footer class="jf-foot" *ngIf="showFooter">
        <span
          class="jf-foot__hint"
          [class.jf-foot__hint--block]="!!missingHint"
          *ngIf="footerHintText">
          <i class="pi pi-info-circle"></i> {{ footerHintText }}
        </span>
        <ng-content select="[shellFooterStart]"></ng-content>
        <span class="jf-foot__spacer"></span>
        <ng-content select="[shellFooter]"></ng-content>
        <button type="button" class="jf-btn" (click)="cancel.emit()">{{ cancelLabel }}</button>
        <span
          class="jf-foot__save-wrap"
          [pTooltip]="saveBlockedTooltip"
          [tooltipDisabled]="canSave || !saveBlockedTooltip"
          tooltipPosition="top">
          <button
            *ngIf="showSave"
            type="button"
            class="jf-btn jf-btn--primary"
            [disabled]="!canSave || saving || loading"
            (click)="save.emit()">
            <i class="pi pi-spin pi-spinner" *ngIf="saving"></i>
            {{ saveLabel }}
          </button>
        </span>
      </footer>
    </div>

    <ng-template #loadingTpl>
      <div class="jf-body jf--loading">
        <p-progressSpinner [style]="{ width: '40px', height: '40px' }"></p-progressSpinner>
      </div>
    </ng-template>
  `,
  host: { class: 'jf-shell-host' },
})
export class FormShellComponent {
  @Input() title = '';
  @Input() subtitle = '';
  @Input() layout: FormShellLayout = 'page';
  @Input() missingFields: string[] = [];
  /** Override footer hint (e.g. "Can't book: …"). Falls back to "Required: …". */
  @Input() missingHint = '';
  /** Shown on the primary button when save is disabled. */
  @Input() saveBlockedTooltip = '';
  @Input() canSave = true;
  @Input() saving = false;
  @Input() loading = false;
  @Input() saveLabel = 'Save';
  @Input() cancelLabel = 'Cancel';
  @Input() showSave = true;
  @Input() showBack = true;
  @Input() showHeader = true;
  @Input() showFooter = true;

  @Output() save = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<void>();

  get footerHintText(): string {
    if (this.missingHint) return this.missingHint;
    if (this.missingFields?.length) return `Required: ${this.missingFields.join(', ')}`;
    return '';
  }
}
